import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, ArrowLeft, Phone, Crown, Gem, Star, Heart, Zap } from 'lucide-react';
import { matchToRateSheet } from '../utils/rateSheetMatch';

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('http') ? url : `${API}${url}`;

const OCCASIONS = {
  wedding: {
    title: 'Wedding Getaway',
    kicker: 'The Occasion Collection',
    icon: Heart,
    headline: ['Your Perfect Exit,', 'Perfectly Chauffeured.'],
    intro: 'Rolls-Royce, Maybach, and Bentley — detailed, ribbon-ready, and driven by professionals. From the ceremony send-off to the bridal party shuttle, ACE handles the getaway.',
    collectionSlug: 'wedding-getaway',
    match: /ghost|cullinan|maybach|mulsanne|bentayga|sprinter limo|sprinter executive/i,
    perks: [
      'Uniformed professional chauffeur',
      'Red-carpet & ribbon decor on request',
      'Champagne-ready interiors',
      'Bridal party Sprinter shuttles available',
      'Photo-stop friendly routing',
      'On-time ceremony coordination',
    ],
  },
  exotic: {
    title: 'The Exotic Collection',
    kicker: 'The Occasion Collection',
    icon: Gem,
    headline: ['Arrive Loud.', 'Leave Unforgettable.'],
    intro: 'Lamborghini, Ferrari, and V12 statements for birthdays, video shoots, proposals, and nights that need an entrance. Chauffeured arrivals or self-drive by request.',
    collectionSlug: 'exotic-collection',
    match: /ferrari|aventador|urus|corvette|g.?wagon|g63|g550|levante/i,
    perks: [
      '48–72hr advance notice',
      'Chauffeured arrivals & photo ops',
      'Video shoot & event packages',
      'Self-drive available on select vehicles',
      'Multi-car convoys on request',
      'Professional detail before every trip',
    ],
  },
};

const OccasionPage = () => {
  const { occasionKey } = useParams();
  const navigate = useNavigate();
  const occasion = OCCASIONS[occasionKey];
  const [mediaEntries, setMediaEntries] = useState([]);
  const [pricingVehicles, setPricingVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    Promise.all([
      fetch(`${API}/api/fleet-media/all`).then(r => r.ok ? r.json() : { vehicles: [] }),
      fetch(`${API}/api/pricing/vehicles`).then(r => r.ok ? r.json() : { vehicles: [] }),
    ]).then(([mediaData, pricingData]) => {
      setMediaEntries(mediaData.vehicles || []);
      setPricingVehicles((pricingData.vehicles || pricingData) || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [occasionKey]);

  const collectionImages = useMemo(() => {
    if (!occasion) return [];
    const entry = mediaEntries.find(m => m.vehicle_slug === occasion.collectionSlug);
    return (entry?.images || []).map(i => i.url);
  }, [mediaEntries, occasion]);

  const vehicles = useMemo(() => {
    if (!occasion) return [];
    const nonRentalPricing = pricingVehicles.filter(p => p.category !== 'rental');
    return mediaEntries
      .filter(m => m.vehicle_slug && m.vehicle_slug !== occasion.collectionSlug && (m.images || []).length > 0)
      .filter(m => occasion.match.test(m.vehicle_name || m.vehicle_slug))
      .filter(m => !/collection|getaway fleet/i.test(m.vehicle_name || ''))
      .map(m => {
        const match = matchToRateSheet(m.vehicle_name || m.vehicle_slug, nonRentalPricing);
        return {
          name: m.vehicle_name || m.vehicle_slug,
          slug: m.vehicle_slug,
          thumbnail: m.images[0]?.url,
          photoCount: m.images.length,
          retail: match?.retail_hourly || 0,
          member: match?.member_hourly || 0,
        };
      })
      .sort((a, b) => (b.retail - a.retail) || a.name.localeCompare(b.name));
  }, [mediaEntries, pricingVehicles, occasion]);

  if (!occasion) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="occasion-not-found">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Collection Not Found</h1>
          <Button onClick={() => navigate('/fleet-gallery')} variant="outline" className="border-gray-700 text-gray-300">Back to Fleet</Button>
        </div>
      </div>
    );
  }

  const Icon = occasion.icon;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="occasion-page">
      {/* Hero */}
      <section className="relative py-20 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <button onClick={() => navigate('/fleet-gallery')} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8" data-testid="back-to-fleet-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Fleet
          </button>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-5 flex items-center gap-2">
                <Icon className="h-4 w-4" /> {occasion.kicker}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6" data-testid="occasion-title">
                {occasion.headline[0]}<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-amber-500">{occasion.headline[1]}</span>
              </h1>
              <p className="text-base text-gray-400 max-w-xl mb-8 leading-relaxed">{occasion.intro}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => document.querySelector('[data-testid="occasion-vehicles"]')?.scrollIntoView({ behavior: 'smooth' })} className="h-12 px-6 bg-red-600 hover:bg-red-700 text-white text-sm font-bold" data-testid="occasion-browse-btn">
                  Shop the Collection <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <a href="tel:6788463009" className="inline-flex items-center justify-center h-12 px-6 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold">
                  <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
                </a>
              </div>
            </div>
            {/* Collection photo mosaic */}
            {collectionImages.length > 0 && (
              <div className="grid grid-cols-2 gap-3" data-testid="occasion-mosaic">
                {collectionImages.slice(0, 4).map((img, i) => (
                  <div key={i} className={`rounded-xl overflow-hidden bg-gray-900 border border-gray-800 ${i === 0 && collectionImages.length >= 3 ? 'row-span-2' : ''}`}>
                    <img src={resolveUrl(img)} alt={`${occasion.title} ${i + 1}`} className="w-full h-full object-cover min-h-[140px]" loading={i === 0 ? 'eager' : 'lazy'} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Perks strip */}
      <section className="border-t border-gray-900 bg-gray-950/50">
        <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="occasion-perks">
          {occasion.perks.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-300">
              <Star className="h-3 w-3 text-red-500 shrink-0" /> {p}
            </div>
          ))}
        </div>
      </section>

      {/* Vehicles */}
      <section className="py-14 border-t border-gray-900" data-testid="occasion-vehicles">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <Crown className="h-5 w-5 text-red-500" />
            <div>
              <h2 className="text-xl font-bold">{occasion.title} Vehicles</h2>
              <p className="text-xs text-gray-500">{vehicles.length} vehicles · professional chauffeur included</p>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-16">
              <div className="h-10 w-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : vehicles.length === 0 ? (
            <p className="text-center text-gray-500 py-16">Vehicles coming soon — call us for availability.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {vehicles.map(v => (
                <Card key={v.slug}
                  className="bg-gray-950 border-gray-800 overflow-hidden group hover:border-gray-600 transition-all cursor-pointer"
                  onClick={() => navigate(`/fleet/${v.slug}`)}
                  data-testid={`occasion-vehicle-${v.slug}`}>
                  <div className="relative h-44 overflow-hidden bg-gray-900">
                    <img src={resolveUrl(v.thumbnail)} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    {v.photoCount > 1 && (
                      <span className="absolute bottom-3 right-3 text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2 py-1 rounded-full">{v.photoCount} photos</span>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="font-bold text-white text-sm group-hover:text-red-500 transition-colors mb-3 truncate">{v.name}</p>
                    <div className="flex items-end justify-between">
                      <div>
                        {v.retail > 0 ? (
                          <>
                            <div className="flex items-baseline gap-1">
                              <p className="text-xl font-black text-white">${v.retail}</p>
                              <p className="text-[10px] text-gray-500">/hr chauffeured</p>
                            </div>
                            {v.member > 0 && v.member < v.retail && (
                              <p className="text-[10px] text-red-400 font-semibold">from ${v.member}/hr member</p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm font-black text-white">By Request</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/booking?vehicle=${v.slug}`); }}
                        className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-full transition-colors"
                        data-testid={`occasion-book-${v.slug}`}
                      >
                        Book <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 border-t border-gray-900 text-center">
        <div className="max-w-lg mx-auto px-6">
          <Zap className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Planning Something Special?</h3>
          <p className="text-sm text-gray-500 mb-6">Tell us the occasion — we'll build the package, decor, and timing around your day.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/booking')} className="h-11 px-6 bg-red-600 hover:bg-red-700 text-white text-sm font-bold" data-testid="occasion-book-cta">
              Book Now <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-11 px-6 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OccasionPage;
